import { useAsyncCallback } from "../../hooks/useAsyncCallback";
import { Button } from "@components/ui/button";
import { Badge } from "@components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@components/ui/card";
import { Empty, EmptyDescription, EmptyHeader, EmptyTitle } from "@components/ui/empty";
import { Skeleton } from "@components/ui/skeleton";
import type { AiContextSnapshot } from "@/lib/auth";
import { buildSnapshotChips, formatSlaLabel } from "@/lib/contextSnapshot";

type AiContextSnapshotPanelProps = {
  title?: string;
  description?: string;
  snapshot: AiContextSnapshot | null;
  isLoading: boolean;
  error?: string | null;
  notice?: string | null;
  isRefreshing?: boolean;
  onRefresh?: () => Promise<void>;
  onCopy?: () => Promise<void>;
  refreshLabel?: string;
  copyLabel?: string;
  emptyTitle?: string;
  emptyDescription?: string;
  refreshDisabled?: boolean;
  copyDisabled?: boolean;
};

export function AiContextSnapshotPanel({
  title = "AI Context Snapshot",
  description = "Automated summary, confidence, and SLA status for this conversation.",
  snapshot,
  isLoading,
  error = null,
  notice = null,
  isRefreshing = false,
  onRefresh,
  onCopy,
  refreshLabel = "Refresh snapshot",
  copyLabel = "Copy snapshot",
  emptyTitle = "No snapshot yet",
  emptyDescription = "Refresh to capture the latest AI context for this thread.",
  refreshDisabled = false,
  copyDisabled = false,
}: AiContextSnapshotPanelProps) {
  const chipLabels = snapshot ? buildSnapshotChips(snapshot) : [];
  const showActions = Boolean((onCopy && snapshot) || onRefresh);
  const isRefreshDisabled = refreshDisabled || isLoading;
  const isCopyDisabled = copyDisabled || !snapshot;

  const { pending: copying, callback: handleCopyClick } = useAsyncCallback(async () => {
    await onCopy?.();
  });
  const { pending: refreshing, callback: handleRefreshClick } = useAsyncCallback(async () => {
    await onRefresh?.();
  });

  return (
    <Card className="border-border bg-card/60">
      <CardHeader className="space-y-2">
        <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
          <div>
            <CardTitle className="text-lg">{title}</CardTitle>
            <CardDescription className="text-muted-foreground">
              {description}
            </CardDescription>
            {notice ? (
              <p className="text-xs text-muted-foreground">{notice}</p>
            ) : null}
          </div>
          {showActions ? (
            <div className="flex flex-wrap gap-2">
              {onCopy && snapshot ? (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleCopyClick}
                  loading={copying} loadingLabel="Copying snapshot…" disabled={isCopyDisabled}
                >
                  {copyLabel}
                </Button>
              ) : null}
              {onRefresh ? (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleRefreshClick}
                  loading={refreshing || isRefreshing} loadingLabel="Refreshing snapshot…" disabled={isRefreshDisabled}
                >
                  {refreshLabel}
                </Button>
              ) : null}
            </div>
          ) : null}
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {error ? (
          <p className="text-sm text-amber-200" role="alert">
            {error}
          </p>
        ) : null}
        {isLoading ? (
          <div className="space-y-2">
            <Skeleton className="h-4 w-60" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-full" />
          </div>
        ) : snapshot ? (
          <>
            {chipLabels.length ? (
              <div className="flex flex-wrap gap-2">
                {chipLabels.map((label) => (
                  <Badge key={label} variant="outline">
                    {label}
                  </Badge>
                ))}
              </div>
            ) : null}
            <ul className="space-y-2 text-sm text-foreground/90">
              {snapshot.insights.map((insight) => (
                <li key={insight} className="flex gap-2">
                  <span className="text-muted-foreground">•</span>
                  <span>{insight}</span>
                </li>
              ))}
            </ul>
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant="outline">
                Priority: {snapshot.priority}
              </Badge>
              <Badge variant="outline">
                {formatSlaLabel(snapshot.slaState)}
              </Badge>
            </div>
            <p className="text-sm text-muted-foreground">
              Confidence: {snapshot.confidence.toFixed(2)} · Impact: {snapshot.impact}
            </p>
            {snapshot.intent ? (
              <p className="text-sm text-muted-foreground">Intent: {snapshot.intent}</p>
            ) : null}
            {snapshot.customer ? (
              <p className="text-sm text-muted-foreground">Customer: {snapshot.customer}</p>
            ) : null}
            {snapshot.triageRule ? (
              <p className="text-sm text-muted-foreground">
                Triage rule: {snapshot.triageRule}
              </p>
            ) : null}
            <p className="text-xs text-muted-foreground">
              Request ID: {snapshot.requestId}
            </p>
            <p className="text-xs text-muted-foreground">
              Last refreshed: {new Date(snapshot.generatedAt).toLocaleString()}
            </p>
            {snapshot.guardrailWarnings.length > 0 ? (
              <div className="rounded-md border border-amber-300/50 bg-amber-500/10 p-3 text-xs text-amber-100">
                <p className="font-semibold uppercase tracking-[0.2em]">Guardrail notes</p>
                <ul className="mt-1 space-y-1">
                  {snapshot.guardrailWarnings.map((warning) => (
                    <li key={warning}>&bull; {warning}</li>
                  ))}
                </ul>
              </div>
            ) : null}
          </>
        ) : (
          <Empty className="border-border/60 bg-muted/40">
            <EmptyHeader>
              <EmptyTitle>{emptyTitle}</EmptyTitle>
              <EmptyDescription className="text-muted-foreground">
                {emptyDescription}
              </EmptyDescription>
            </EmptyHeader>
          </Empty>
        )}
      </CardContent>
    </Card>
  );
}
